import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { executeFetchDeviceMetrics } from 'modules/Telematics/queries';
import { useAppSelector } from 'store/hooks';

import useApolloClientAndMsalAuth from 'customHooks/useApolloClientAndMsalAuth';

import { MetricsResponse } from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/types';
import { processMetricsDataList } from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/utils';
import Graph from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/Graph';
import { INIT_COMBINED_GRAPHICAL_METRICS_DATA } from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/constants';
import moment from 'moment';
import { Box } from '@mui/material';

const LiveSummary = () => {
	const machineId = useAppSelector((s) => s.router.location.query.machineId);
	const { client } = useApolloClientAndMsalAuth();
	const apolloClient = useApolloClient(client);
	const [combinedGraphicalMetricData, setCombinedGraphicalMetricData] =
		useState(INIT_COMBINED_GRAPHICAL_METRICS_DATA);
	const getDeviceMetric = useCallback(async () => {
		try {
			const startTime =
				moment().startOf('day').subtract(1, 'days').toDate().getTime() /
				1000;
			// const startTime =
			// 	moment('2023-11-08').startOf('day').toDate().getTime() / 1000;
			// const startTime = 1696636800;
			const response: MetricsResponse = await executeFetchDeviceMetrics({
				machineNumber: machineId,
				// machineNumber: 'UPL_1156',
				// machineNumber: 'UPL_1550',
				startTime,
				duration: 86400,
				apolloClient,
			});
			if (
				response.data.GetProcessMetric.responseStatus.status ===
				'SUCCESS'
			) {
				const processedData = processMetricsDataList(
					response.data.GetProcessMetric.metricSummary,
				);
				setCombinedGraphicalMetricData(processedData);
			}
		} catch (err) {
			// eslint-disable-next-line no-empty
		} finally {
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apolloClient, machineId]);

	useEffect(() => {
		getDeviceMetric();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [machineId]);
	const calculatedMetrics = useMemo(() => {
		const LABELS = {
			SPRAYER_ON: 'Sprayer on',
			EXTERNAL_POWER_DISCONNECTED: 'External power disconnected',
			ENGINE_OFF: 'Engine off',
			INTERNAL_BATTERY_LOW: 'Internal battery low',
		};
		const SUMMARY_LIST = [
			'SPRAYER_ON',
			'EXTERNAL_POWER_DISCONNECTED',
			'ENGINE_OFF',
			'INTERNAL_BATTERY_LOW',
		] as Array<keyof typeof combinedGraphicalMetricData>;

		return SUMMARY_LIST.map((metricKey) => {
			let totalActiveSeconds = 0;
			combinedGraphicalMetricData[metricKey].graphData.forEach((m) => {
				totalActiveSeconds += Math.abs(
					moment(m.startTime).diff(moment(m.endTime), 'seconds'),
				);
			});
			return {
				metricLabel: LABELS[metricKey as keyof typeof LABELS],
				metricValue:
					((totalActiveSeconds * 100) / (24 * 60 * 60)).toFixed(2) +
					'%',
			};
		});
	}, [combinedGraphicalMetricData]);
	return (
		<Box>
			{calculatedMetrics.map((metric, index) => {
				return (
					<Box key={index} sx={{ padding: '5px 0px' }}>
						{metric.metricLabel}: <b>{metric.metricValue}</b>
					</Box>
				);
			})}
			<Box marginTop="15px"></Box>
			<Graph
				combinedGraphicalMetricData={combinedGraphicalMetricData}
			></Graph>
		</Box>
	);
};

export default LiveSummary;
